import { ALPHABET_SIZE, letterIndex } from "@/lib/tool-kit/ciphers/alphabet";

/**
 * How often each letter turns up in ordinary English text, A–Z, as a proportion.
 *
 * The widely reproduced table from Robert Lewand's *Cryptological Mathematics* (2000).
 * Any large English corpus gives figures within a fraction of a percent of these, which
 * is all a ranking needs.
 */
export const ENGLISH_LETTER_FREQUENCIES: readonly number[] = [
  0.081_67, 0.014_92, 0.027_82, 0.042_53, 0.127_02, 0.022_28, 0.020_15,
  0.060_94, 0.069_66, 0.001_53, 0.007_72, 0.040_25, 0.024_06, 0.067_49,
  0.075_07, 0.019_29, 0.000_95, 0.059_87, 0.063_27, 0.090_56, 0.027_58,
  0.009_78, 0.0236, 0.0015, 0.019_74, 0.000_74,
];

/**
 * Pearson's chi-squared statistic of a text's letter counts against English: the sum
 * of (observed − expected)² / expected over A–Z. Lower is more English-like.
 *
 * A text with no letters scores `Infinity`, so it sorts last. On fewer than a few dozen
 * letters the statistic is noisy — the ranking is a hint, not a verdict.
 */
export function englishChiSquared(text: string): number {
  const counts: number[] = Array.from({ length: ALPHABET_SIZE }, () => 0);
  let total = 0;
  for (const char of text) {
    const index = letterIndex(char);
    if (index >= 0) {
      counts[index] += 1;
      total += 1;
    }
  }
  if (total === 0) {
    return Number.POSITIVE_INFINITY;
  }
  let score = 0;
  for (const [index, observed] of counts.entries()) {
    const expected = ENGLISH_LETTER_FREQUENCIES[index] * total;
    score += (observed - expected) ** 2 / expected;
  }
  return score;
}
